<script setup lang="ts">
import type { Locale } from '@/settings/langs'
import { maintenanceEnabled, skipMaintenance } from '@/components/layout/Maintenance/Maintenance.ts'
import { i18nLocale } from '@/compositions/i18nPlugin'
import { onMountedApp } from '@/compositions/onMountedApp'
import { elplusLocaleMap } from '@/settings/langs'

const elplusLocale = computed(() => elplusLocaleMap[i18nLocale.value as Locale])

const { triggerApi, isLoggedGoat, routeGame, isProd } = gamesUtils
const gameStore = useGameStore()
const apiModeBadge = computed(() => {
  if (!routeGame.value || isProd)
    return null
  const mode = gameStore.devApiMode[routeGame.value.gameKey]
  if (mode === 'timeshift')
    return { label: 'Timeshift API', cls: 'border-amber-400/40 bg-amber-500/90 shadow-amber-500/25' }
  if (mode === 'local')
    return { label: 'Local API', cls: 'border-emerald-400/40 bg-emerald-500/90 shadow-emerald-500/25' }
  return null
})
triggerApi.initTriggerApi()
const route = useRoute()
onMounted(() => {
  if (route.query.forceVisibility) {
    // forceVisibility=soccer-clash
    localStorage?.setItem?.(`id-visible_${route.query.forceVisibility}`, 'on')
    delete route.query.forceVisibility
    window.location.href = route.path
  }

  if (maintenanceEnabled.value) {
    const tokenMaintenance: string = import.meta.env.VITE_MAINTENANCE_TOKEN || ''
    if (route.query.skipMaintenance === tokenMaintenance) {
      localStorage?.setItem?.('s-m', String(route.query.skipMaintenance))
      maintenanceEnabled.value = false
      skipMaintenance.value = true
      delete route.query.skipMaintenance
      window.location.href = route.path
    } else if (localStorage?.getItem?.('s-m') === tokenMaintenance) {
      maintenanceEnabled.value = false
      skipMaintenance.value = true
    } else {
      maintenanceEnabled.value = true
      skipMaintenance.value = false
    }
  }
  onMountedApp()
})
</script>

<template>
  <el-config-provider :locale="elplusLocale">
    <maintenance v-if="maintenanceEnabled && route.params.gameID === 'questland'" />
    <template v-else>
      <install-prompt-component />
      <page-header />
      <Transition name="ts-badge">
        <div
          v-if="apiModeBadge"
          class="pointer-events-none fixed right-3 top-[68px] z-[70] big:right-4 big:top-[75px]"
        >
          <div
            class="flex items-center gap-2 rounded-xl border px-3 py-2 shadow-lg backdrop-blur-sm"
            :class="[apiModeBadge.cls]"
          >
            <span class="h-2 w-2 shrink-0 animate-pulse rounded-full bg-white" />
            <span class="text-xs font-bold uppercase leading-none tracking-wider text-white">{{ apiModeBadge.label }}</span>
          </div>
        </div>
      </Transition>
      <router-view />
      <page-footer />
      <log-in-dialog />
      <offer-dialog-component />
      <pay-pro-component />
      <buy-free-dialog-component />
      <manage-cookies-component />
      <layout-page-loader-component />
      <goat-benefit-dialog />
      <template v-if="isLoggedGoat">
        <goat-connect-game-dialog />
      </template>
      <game-user-box-dialog v-else />
      <cart-drawer-component />
      <rewards-popup />
    </template>
  </el-config-provider>
</template>

<style scoped>
.ts-badge-enter-active,
.ts-badge-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.ts-badge-enter-from,
.ts-badge-leave-to {
  opacity: 0;
  transform: translateX(8px);
}
</style>
